// hooks/useCanvasLogic.js
import { useState, useRef, useEffect, useCallback } from 'react';
import ENV from '../config/env';

export const useCanvasLogic = () => {
    const [logos, setLogos] = useState([]);
    const [selectedLogo, setSelectedLogo] = useState(null);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [selectedProductImage, setSelectedProductImage] = useState(null);
    const [currentProductImages, setCurrentProductImages] = useState([]);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [mainProducts, setMainProducts] = useState([]);
    const [partnerProducts, setPartnerProducts] = useState([]);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [mainCategories, setMainCategories] = useState([]);
    const [partnerCategories, setPartnerCategories] = useState([]);
    const [productCategories, setProductCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [activeSource, setActiveSource] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedVariant, setSelectedVariant] = useState(null);

    const canvasRef = useRef(null);
    const containerRef = useRef(null);
    const imagesRef = useRef({});
    const productImagesRef = useRef({});
    const isDraggingRef = useRef(false);
    const dragInfoRef = useRef({ type: null, startX: 0, startY: 0, initial: {} });
    // Tracks the image URL set by injectProduct so the effect doesn't override it
    const injectedImageRef = useRef(null);

    // Helper: Get all images from a main product including variants
    const getAllProductImages = useCallback((product) => {
        let allImages = [];

        // Add main product images
        if (product.images && product.images.length > 0) {
            allImages.push(...product.images);
        }

        // Add variant images
        if (product.variants && product.variants.length > 0) {
            product.variants.forEach(variant => {
                if (variant.images && variant.images.length > 0) {
                    allImages.push(...variant.images);
                }
            });
        }

        // Remove duplicates
        const uniqueImages = [...new Set(allImages)];
        console.log(`Product ${product.name} has ${uniqueImages.length} total images`);
        return uniqueImages;
    }, []);

    // Load main products from API
    const loadMainProducts = useCallback(async () => {
        try {
            const response = await fetch(`${ENV.API_BASE}/products`);
            if (response.ok) {
                const products = await response.json();
                return products.map((product) => {
                    const allProductImages = getAllProductImages(product);

                    return {
                        id: `main-${product.id}`,
                        originalId: product.id,
                        name: product.name,
                        category: product.category || 'General',
                        subCategory: product.subCategory || '',
                        brand: product.brand || '',
                        images: allProductImages.map(img => ENV.FILE_SERVER_URL + img),
                        originalProduct: product,
                        variants: product.variants || [],
                        source: 'main',
                        sourceLabel: 'Print Studio'
                    };
                });
            }
            return [];
        } catch (error) {
            console.error('Error loading main products:', error);
            return [];
        }
    }, [getAllProductImages]);

    // Load ALL partner products from the JSON cache
    const loadPartnerProducts = useCallback(async () => {
        try {
            console.log('Loading ALL partner products from cache...');
            const response = await fetch(`${ENV.API_BASE}/partner-products/products/all`);

            if (response.ok) {
                const data = await response.json();
                console.log(`Received ${data.totalCount} partner products total`);

                if (data.products && data.products.length > 0) {
                    return data.products.map((product) => ({
                        id: `partner-${product.id}`,
                        originalId: product.id,
                        name: product.name,
                        category: product.category || 'General',
                        subCategory: product.subCategory || '',
                        brand: product.brand || '',
                        images: (product.allImages || []).map(imgUrl =>
                            `${ENV.API_BASE}/partner-products/image-proxy?url=${encodeURIComponent(imgUrl)}`
                        ),
                        source: 'partner',
                        sourceLabel: 'Promocionales',
                        originalProduct: product
                    }));
                } else {
                    console.warn('No partner products in cache');
                    return [];
                }
            } else {
                console.error('Failed to load partner products:', response.status);
                return [];
            }
        } catch (error) {
            console.error('Error loading partner products:', error);
            return [];
        }
    }, []);

    // Load all products
    const loadProducts = useCallback(async () => {
        try {
            setIsLoading(true);

            const [main, partner] = await Promise.all([
                loadMainProducts(),
                loadPartnerProducts()
            ]);

            setMainProducts(main);
            setPartnerProducts(partner);

            const mainCats = [...new Set(main.map(p => p.category).filter(Boolean))].sort();
            const partnerCats = [...new Set(partner.map(p => p.category).filter(Boolean))].sort();

            setMainCategories(mainCats);
            setPartnerCategories(partnerCats);

            const allProducts = [...main, ...partner];
            setFilteredProducts(allProducts);
            setProductCategories([...new Set([...mainCats, ...partnerCats])].sort());

        } catch (error) {
            console.error('Error loading products:', error);
        } finally {
            setIsLoading(false);
        }
    }, [loadMainProducts, loadPartnerProducts]);

    // Load products on mount
    useEffect(() => {
        loadProducts();
    }, [loadProducts]);

    // Update filtered products when filters change
    useEffect(() => {
        let sourceProducts = [];

        if (activeSource === 'all') {
            sourceProducts = [...mainProducts, ...partnerProducts];
            const allCats = [...new Set([...mainCategories, ...partnerCategories])].sort();
            setProductCategories(allCats);
        } else if (activeSource === 'main') {
            sourceProducts = [...mainProducts];
            setProductCategories(mainCategories);
        } else if (activeSource === 'partner') {
            sourceProducts = [...partnerProducts];
            setProductCategories(partnerCategories);
        }

        let filtered = sourceProducts;
        if (selectedCategory) {
            filtered = sourceProducts.filter(p => p.category === selectedCategory);
        }

        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase().trim();
            filtered = filtered.filter(p =>
                p.name.toLowerCase().includes(term) ||
                (p.brand || '').toLowerCase().includes(term)
            );
        }

        setFilteredProducts(filtered);

        if (selectedProduct && !filtered.find(p => p.id === selectedProduct.id)) {
            setSelectedProduct(null);
            setSelectedProductImage(null);
            setSelectedVariant(null);
            setCurrentProductImages([]);
            setCurrentImageIndex(0);
        }
    }, [mainProducts, partnerProducts, activeSource, selectedCategory, searchTerm, mainCategories, partnerCategories, selectedProduct]);

    // Update images when selected product or variant changes
    useEffect(() => {
        if (!selectedProduct) {
            setCurrentProductImages([]);
            return;
        }

        console.log('Updating images for product:', selectedProduct.name, 'source:', selectedProduct.source);

        // For partner products
        if (selectedProduct.source === 'partner') {
            if (selectedProduct.images && selectedProduct.images.length > 0) {
                console.log('Partner product images:', selectedProduct.images.length);
                setCurrentProductImages(selectedProduct.images);
                // If injectProduct set a specific image, honour it and clear the ref
                if (injectedImageRef.current) {
                    setSelectedProductImage(injectedImageRef.current);
                    const idx = selectedProduct.images.indexOf(injectedImageRef.current);
                    setCurrentImageIndex(idx >= 0 ? idx : 0);
                    injectedImageRef.current = null;
                } else {
                    setSelectedProductImage(selectedProduct.images[0]);
                    setCurrentImageIndex(0);
                }
            }
            return;
        }

        // For main products with variants
        const originalProduct = selectedProduct.originalProduct;
        if (!originalProduct) return;

        let imagesToLoad = [];
        const variantSku = selectedVariant?.sku;

        console.log('Variant SKU:', variantSku);

        if (!variantSku || variantSku === 'general-default') {
            // For "Vista general", load ALL images from the product
            // This includes main product images AND all variant images

            // Add main product images
            if (originalProduct.images && originalProduct.images.length > 0) {
                imagesToLoad.push(...originalProduct.images.map(img => ENV.FILE_SERVER_URL + img));
            }

            // Add all variant images
            if (originalProduct.variants && originalProduct.variants.length > 0) {
                originalProduct.variants.forEach(variant => {
                    if (variant.images && variant.images.length > 0) {
                        imagesToLoad.push(...variant.images.map(img => ENV.FILE_SERVER_URL + img));
                    }
                });
            }

            // Remove duplicates
            imagesToLoad = [...new Set(imagesToLoad)];
            console.log('Loading Vista general (all images):', imagesToLoad.length);
        } else {
            // Find the specific variant
            const variant = originalProduct.variants?.find(v => v.sku === variantSku);
            if (variant && variant.images && variant.images.length > 0) {
                imagesToLoad = variant.images.map(img => ENV.FILE_SERVER_URL + img);
                console.log('Loading variant images:', variant.color, imagesToLoad.length);
            } else {
                // Fallback to main images if variant has none
                imagesToLoad = (originalProduct.images || []).map(img => ENV.FILE_SERVER_URL + img);
                console.log('Variant has no images, falling back to main images:', imagesToLoad.length);
            }
        }

        console.log('Final images to load:', imagesToLoad.length);
        setCurrentProductImages(imagesToLoad);

        if (imagesToLoad.length > 0) {
            // If injectProduct set a specific image, honour it and clear the ref
            if (injectedImageRef.current) {
                setSelectedProductImage(injectedImageRef.current);
                const idx = imagesToLoad.indexOf(injectedImageRef.current);
                setCurrentImageIndex(idx >= 0 ? idx : 0);
                injectedImageRef.current = null;
            } else {
                // Only reset to first image if current image is not part of this product's set
                const alreadyLoaded = imagesToLoad.includes(selectedProductImage);
                if (!alreadyLoaded) {
                    setSelectedProductImage(imagesToLoad[0]);
                    setCurrentImageIndex(0);
                }
            }
        }
    }, [selectedProduct, selectedVariant]);

    // Load specific image to canvas when selectedProductImage changes
    useEffect(() => {
        if (!selectedProduct || !selectedProductImage) return;

        const loadSpecificImage = async () => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.src = selectedProductImage;

            img.onload = () => {
                productImagesRef.current[selectedProduct.id] = img;
                redraw();
            };

            img.onerror = () => {
                console.error('Failed to load image:', selectedProductImage);
                const fallbackImg = new Image();
                fallbackImg.crossOrigin = 'anonymous';
                fallbackImg.src = `${ENV.FILE_SERVER_URL}/images/default-product.jpg`;
                fallbackImg.onload = () => {
                    productImagesRef.current[selectedProduct.id] = fallbackImg;
                    redraw();
                };
            };
        };

        loadSpecificImage();
    }, [selectedProductImage, selectedProduct]);

    // Initialize canvas
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const updateCanvasSize = () => {
            if (containerRef.current) {
                const containerWidth = containerRef.current.clientWidth;
                const containerHeight = Math.min(containerWidth * 1.2, 600);
                canvas.width = containerWidth;
                canvas.height = containerHeight;
                redraw();
            }
        };

        updateCanvasSize();

        let resizeTimeout;
        const handleResize = () => {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(updateCanvasSize, 100);
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
            clearTimeout(resizeTimeout);
        };
    }, []);

    // Draw watermark pattern
    const drawWatermarkPattern = (ctx, canvas) => {
        const patternCanvas = document.createElement('canvas');
        const pctx = patternCanvas.getContext('2d');

        patternCanvas.width = 200;
        patternCanvas.height = 200;

        pctx.clearRect(0, 0, 200, 200);
        pctx.globalAlpha = 0.25;
        pctx.fillStyle = '#ffffff';
        pctx.font = 'bold 30px Arial';
        pctx.textAlign = 'center';
        pctx.textBaseline = 'middle';
        pctx.translate(100, 100);
        pctx.rotate(-30 * Math.PI / 180);
        pctx.fillText('PRINT STUDIO', 0, 0);

        const pattern = ctx.createPattern(patternCanvas, 'repeat');
        ctx.fillStyle = pattern;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
    };

    // Redraw canvas
    const redraw = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = '#1a202c';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        const productImg = selectedProduct ? productImagesRef.current[selectedProduct.id] : null;

        if (productImg && productImg.complete && productImg.naturalWidth > 0) {
            const centerX = canvas.width / 2;
            const centerY = canvas.height / 2;
            const maxWidth = Math.min(canvas.width * 0.8, 400);
            const maxHeight = Math.min(canvas.height * 0.8, 500);

            const imgAspect = productImg.width / productImg.height;
            const containerAspect = maxWidth / maxHeight;

            let drawWidth, drawHeight;

            if (imgAspect > containerAspect) {
                drawWidth = maxWidth;
                drawHeight = maxWidth / imgAspect;
            } else {
                drawHeight = maxHeight;
                drawWidth = maxHeight * imgAspect;
            }

            ctx.save();
            ctx.beginPath();
            ctx.rect(
                centerX - maxWidth / 2,
                centerY - maxHeight / 2,
                maxWidth,
                maxHeight
            );
            ctx.clip();

            ctx.drawImage(
                productImg,
                centerX - drawWidth / 2,
                centerY - drawHeight / 2,
                drawWidth,
                drawHeight
            );
            ctx.restore();

            ctx.strokeStyle = '#4a5568';
            ctx.lineWidth = 2;
            ctx.strokeRect(
                centerX - maxWidth / 2,
                centerY - maxHeight / 2,
                maxWidth,
                maxHeight
            );
        }

        logos.forEach((logo, index) => {
            const img = imagesRef.current[logo.id];
            if (!img) return;

            ctx.save();
            ctx.translate(logo.x, logo.y);
            ctx.rotate(logo.rotation * Math.PI / 180);

            ctx.drawImage(
                img,
                -logo.width / 2,
                -logo.height / 2,
                logo.width,
                logo.height
            );

            if (index === selectedLogo) {
                ctx.strokeStyle = '#00aaeb';
                ctx.lineWidth = 2;
                ctx.setLineDash([5, 5]);
                ctx.strokeRect(
                    -logo.width / 2 - 5,
                    -logo.height / 2 - 5,
                    logo.width + 10,
                    logo.height + 10
                );
                ctx.setLineDash([]);

                ctx.fillStyle = '#ffc805';
                const handleY = -logo.height / 2 - 40;
                ctx.beginPath();
                ctx.arc(0, handleY, 10, 0, Math.PI * 2);
                ctx.fill();

                ctx.beginPath();
                ctx.moveTo(0, -logo.height / 2);
                ctx.lineTo(0, handleY);
                ctx.strokeStyle = '#ffc805';
                ctx.lineWidth = 2;
                ctx.stroke();

                ctx.fillStyle = '#00aaeb';
                const handleSize = 8;
                const corners = [
                    { x: -logo.width / 2, y: -logo.height / 2 },
                    { x: logo.width / 2, y: -logo.height / 2 },
                    { x: -logo.width / 2, y: logo.height / 2 },
                    { x: logo.width / 2, y: logo.height / 2 }
                ];
                corners.forEach(corner => {
                    ctx.fillRect(corner.x - handleSize / 2, corner.y - handleSize / 2, handleSize, handleSize);
                });
            }

            ctx.restore();
        });

        drawWatermarkPattern(ctx, canvas);
    }, [logos, selectedLogo, selectedProduct]);

    useEffect(() => {
        redraw();
    }, [logos, selectedLogo, selectedProduct, redraw]);

    // Navigation functions
    const nextImage = useCallback(() => {
        if (currentProductImages.length === 0) return;
        const nextIndex = (currentImageIndex + 1) % currentProductImages.length;
        setCurrentImageIndex(nextIndex);
        setSelectedProductImage(currentProductImages[nextIndex]);
    }, [currentProductImages, currentImageIndex]);

    const prevImage = useCallback(() => {
        if (currentProductImages.length === 0) return;
        const prevIndex = (currentImageIndex - 1 + currentProductImages.length) % currentProductImages.length;
        setCurrentImageIndex(prevIndex);
        setSelectedProductImage(currentProductImages[prevIndex]);
    }, [currentProductImages, currentImageIndex]);

    const handleFileUpload = (event) => {
        const files = Array.from(event.target.files);

        if (logos.length + files.length > 2) {
            alert('Máximo 2 logos permitidos');
            return;
        }

        files.forEach((file, index) => {
            if (!file.type.startsWith('image/')) {
                alert('Por favor, sube solo archivos de imagen');
                return;
            }

            const reader = new FileReader();
            reader.onload = (e) => {
                const img = new Image();
                img.onload = () => {
                    const canvas = canvasRef.current;
                    if (!canvas) return;

                    const maxWidth = canvas.width * 0.3;
                    const maxHeight = canvas.height * 0.3;
                    let width = img.width;
                    let height = img.height;

                    if (width > maxWidth || height > maxHeight) {
                        const ratio = Math.min(maxWidth / width, maxHeight / height);
                        width *= ratio;
                        height *= ratio;
                    }

                    const logoId = Date.now() + index;
                    const newLogo = {
                        id: logoId,
                        x: canvas.width / 2 + (index * 50),
                        y: canvas.height / 2 + (index * 50),
                        width,
                        height,
                        rotation: 0,
                        originalWidth: img.width,
                        originalHeight: img.height
                    };

                    imagesRef.current[logoId] = img;
                    setLogos(prev => [...prev, newLogo]);
                    setSelectedLogo(prev => prev === null ? 0 : prev);
                };
                img.src = e.target.result;
            };
            reader.readAsDataURL(file);
        });

        event.target.value = '';
    };

    const getCanvasCoords = (clientX, clientY) => {
        const canvas = canvasRef.current;
        if (!canvas) return { x: 0, y: 0 };
        const rect = canvas.getBoundingClientRect();
        return {
            x: clientX - rect.left,
            y: clientY - rect.top
        };
    };

    const pointInRotatedRect = (px, py, rx, ry, width, height, rotation) => {
        const angle = -rotation * Math.PI / 180;
        const cos = Math.cos(angle);
        const sin = Math.sin(angle);
        const dx = px - rx;
        const dy = py - ry;
        const localX = dx * cos - dy * sin;
        const localY = dx * sin + dy * cos;
        return Math.abs(localX) < width / 2 && Math.abs(localY) < height / 2;
    };

    const handlePointerDown = (clientX, clientY) => {
        const { x, y } = getCanvasCoords(clientX, clientY);

        if (selectedLogo !== null) {
            const logo = logos[selectedLogo];
            const angle = -logo.rotation * Math.PI / 180;
            const cos = Math.cos(angle);
            const sin = Math.sin(angle);
            const dx = x - logo.x;
            const dy = y - logo.y;
            const localX = dx * cos - dy * sin;
            const localY = dx * sin + dy * cos;
            const handleY = -logo.height / 2 - 40;
            const isNearRotationHandle = Math.abs(localX) < 20 && localY < handleY + 20 && localY > handleY - 20;
            const cornerThreshold = 15;
            const isNearCorner =
                Math.abs(localX) > logo.width / 2 - cornerThreshold &&
                Math.abs(localY) > logo.height / 2 - cornerThreshold;
            const isInsideLogo = Math.abs(localX) < logo.width / 2 && Math.abs(localY) < logo.height / 2;

            if (isNearRotationHandle) {
                isDraggingRef.current = true;
                dragInfoRef.current = {
                    type: 'rotate',
                    startX: x,
                    startY: y,
                    initial: { rotation: logo.rotation }
                };
            } else if (isNearCorner) {
                isDraggingRef.current = true;
                dragInfoRef.current = {
                    type: 'scale',
                    startX: x,
                    startY: y,
                    initial: { width: logo.width, height: logo.height }
                };
            } else if (isInsideLogo) {
                isDraggingRef.current = true;
                dragInfoRef.current = {
                    type: 'move',
                    startX: x,
                    startY: y,
                    initial: { x: logo.x, y: logo.y }
                };
            } else {
                setSelectedLogo(null);
                isDraggingRef.current = false;
            }
        } else {
            for (let i = logos.length - 1; i >= 0; i--) {
                const logo = logos[i];
                if (pointInRotatedRect(x, y, logo.x, logo.y, logo.width, logo.height, logo.rotation)) {
                    setSelectedLogo(i);
                    isDraggingRef.current = true;
                    dragInfoRef.current = {
                        type: 'move',
                        startX: x,
                        startY: y,
                        initial: { x: logo.x, y: logo.y }
                    };
                    break;
                }
            }
        }
    };

    const handlePointerMove = (clientX, clientY) => {
        if (!isDraggingRef.current || selectedLogo === null) return;
        const { x, y } = getCanvasCoords(clientX, clientY);
        const logo = logos[selectedLogo];
        const dragInfo = dragInfoRef.current;

        if (dragInfo.type === 'move') {
            const deltaX = x - dragInfo.startX;
            const deltaY = y - dragInfo.startY;
            setLogos(prev => prev.map((l, i) =>
                i === selectedLogo ? {
                    ...l,
                    x: dragInfo.initial.x + deltaX,
                    y: dragInfo.initial.y + deltaY
                } : l
            ));
        }
        else if (dragInfo.type === 'rotate') {
            const dx = x - logo.x;
            const dy = y - logo.y;
            const angle = Math.atan2(dy, dx) * 180 / Math.PI;
            const rotation = (angle + 90) % 360;
            setLogos(prev => prev.map((l, i) =>
                i === selectedLogo ? { ...l, rotation } : l
            ));
        }
        else if (dragInfo.type === 'scale') {
            const dx = x - logo.x;
            const dy = y - logo.y;
            const distance = Math.sqrt(dx * dx + dy * dy);
            const startDx = dragInfo.startX - logo.x;
            const startDy = dragInfo.startY - logo.y;
            const startDistance = Math.sqrt(startDx * startDx + startDy * startDy);
            if (startDistance === 0) return;
            const scale = distance / startDistance;
            const newWidth = Math.max(30, Math.min(logo.originalWidth, dragInfo.initial.width * scale));
            const newHeight = Math.max(30, Math.min(logo.originalHeight, dragInfo.initial.height * scale));
            setLogos(prev => prev.map((l, i) =>
                i === selectedLogo ? { ...l, width: newWidth, height: newHeight } : l
            ));
        }
    };

    const handlePointerUp = () => {
        isDraggingRef.current = false;
    };

    const handleMouseDown = (e) => {
        e.preventDefault();
        handlePointerDown(e.clientX, e.clientY);
    };

    const handleMouseMove = (e) => {
        if (!isDraggingRef.current) return;
        e.preventDefault();
        handlePointerMove(e.clientX, e.clientY);
    };

    const handleMouseUp = () => {
        handlePointerUp();
    };

    const handleTouchStart = (e) => {
        e.preventDefault();
        if (e.touches.length === 1) {
            const touch = e.touches[0];
            handlePointerDown(touch.clientX, touch.clientY);
        }
    };

    const handleTouchMove = (e) => {
        if (!isDraggingRef.current || e.touches.length !== 1) return;
        e.preventDefault();
        const touch = e.touches[0];
        handlePointerMove(touch.clientX, touch.clientY);
    };

    const handleTouchEnd = (e) => {
        e.preventDefault();
        handlePointerUp();
    };

    const handleWheel = (e) => {
        e.preventDefault();
        if (selectedLogo === null) return;
        const delta = e.deltaY > 0 ? 0.9 : 1.1;
        scaleLogo(delta);
    };

    const rotateLogo = (degrees) => {
        if (selectedLogo === null) return;
        setLogos(prev => prev.map((logo, i) =>
            i === selectedLogo ? { ...logo, rotation: (logo.rotation + degrees) % 360 } : logo
        ));
    };

    const scaleLogo = (factor) => {
        if (selectedLogo === null) return;
        setLogos(prev => prev.map((logo, i) => {
            if (i !== selectedLogo) return logo;
            const newWidth = Math.max(30, Math.min(logo.originalWidth, logo.width * factor));
            const newHeight = Math.max(30, Math.min(logo.originalHeight, logo.height * factor));
            return { ...logo, width: newWidth, height: newHeight };
        }));
    };

    const deleteLogo = () => {
        if (selectedLogo === null) return;
        const logoId = logos[selectedLogo].id;
        delete imagesRef.current[logoId];
        setLogos(prev => prev.filter((_, i) => i !== selectedLogo));
        setSelectedLogo(null);
    };

    const resetAll = () => {
        setLogos([]);
        setSelectedLogo(null);
        setSelectedProduct(null);
        setSelectedProductImage(null);
        setSelectedVariant(null);
        setCurrentProductImages([]);
        setCurrentImageIndex(0);
        setSelectedCategory('');
        setActiveSource('all');
        setSearchTerm('');
        imagesRef.current = {};
    };

    const drawWatermarkOnCanvas = (ctx, canvas) => {
        const patternCanvas = document.createElement('canvas');
        const pctx = patternCanvas.getContext('2d');
        patternCanvas.width = 200;
        patternCanvas.height = 200;
        pctx.clearRect(0, 0, 200, 200);
        pctx.globalAlpha = 0.25;
        pctx.fillStyle = '#ffffff';
        pctx.font = 'bold 30px Arial';
        pctx.textAlign = 'center';
        pctx.textBaseline = 'middle';
        pctx.translate(100, 100);
        pctx.rotate(-30 * Math.PI / 180);
        pctx.fillText('PRINT STUDIO', 0, 0);
        const pattern = ctx.createPattern(patternCanvas, 'repeat');
        ctx.fillStyle = pattern;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
    };

    const captureAndSendQuote = useCallback(async (contactInfo) => {
        const canvas = canvasRef.current;
        if (!canvas) {
            return { success: false, error: 'Canvas no disponible' };
        }

        try {
            const tempCanvas = document.createElement('canvas');
            const tempCtx = tempCanvas.getContext('2d');
            tempCanvas.width = canvas.width;
            tempCanvas.height = canvas.height;

            tempCtx.clearRect(0, 0, tempCanvas.width, tempCanvas.height);
            tempCtx.fillStyle = '#1a202c';
            tempCtx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);

            const productImg = selectedProduct ? productImagesRef.current[selectedProduct.id] : null;

            if (productImg && productImg.complete && productImg.naturalWidth > 0) {
                const centerX = tempCanvas.width / 2;
                const centerY = tempCanvas.height / 2;
                const maxWidth = Math.min(tempCanvas.width * 0.8, 400);
                const maxHeight = Math.min(tempCanvas.height * 0.8, 500);

                const imgAspect = productImg.width / productImg.height;
                const containerAspect = maxWidth / maxHeight;

                let drawWidth, drawHeight;
                if (imgAspect > containerAspect) {
                    drawWidth = maxWidth;
                    drawHeight = maxWidth / imgAspect;
                } else {
                    drawHeight = maxHeight;
                    drawWidth = maxHeight * imgAspect;
                }

                tempCtx.save();
                tempCtx.beginPath();
                tempCtx.rect(
                    centerX - maxWidth / 2,
                    centerY - maxHeight / 2,
                    maxWidth,
                    maxHeight
                );
                tempCtx.clip();

                tempCtx.drawImage(
                    productImg,
                    centerX - drawWidth / 2,
                    centerY - drawHeight / 2,
                    drawWidth,
                    drawHeight
                );
                tempCtx.restore();

                tempCtx.strokeStyle = '#4a5568';
                tempCtx.lineWidth = 2;
                tempCtx.strokeRect(
                    centerX - maxWidth / 2,
                    centerY - maxHeight / 2,
                    maxWidth,
                    maxHeight
                );
            }

            logos.forEach((logo, index) => {
                const img = imagesRef.current[logo.id];
                if (!img) return;

                tempCtx.save();
                tempCtx.translate(logo.x, logo.y);
                tempCtx.rotate(logo.rotation * Math.PI / 180);
                tempCtx.drawImage(
                    img,
                    -logo.width / 2,
                    -logo.height / 2,
                    logo.width,
                    logo.height
                );
                tempCtx.restore();
            });

            drawWatermarkOnCanvas(tempCtx, tempCanvas);

            const imageData = tempCanvas.toDataURL('image/png');
            const sessionId = `${contactInfo.name.replace(/\s+/g, '_')}_${Date.now()}`;

            const response = await fetch(`${ENV.API_BASE}/quote-images/upload`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    image: imageData,
                    sessionId: sessionId
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Error al subir la imagen');
            }

            const data = await response.json();

            return {
                success: true,
                imageUrl: data.imageUrl,
                filename: data.filename,
                expiresIn: data.expiresIn
            };

        } catch (error) {
            console.error('Error capturing image:', error);
            return { success: false, error: error.message };
        }
    }, [logos, selectedProduct]);

    const clearCategory = () => {
        setSelectedCategory('');
    };

    const selectVariant = useCallback((variantSku) => {
        setSelectedVariant({ sku: variantSku });
    }, []);

    // Receive a product payload from ProductDetailModal and apply it to the canvas
    const injectProduct = useCallback((payload) => {
        const { overlayProduct, imageUrl, imageIndex, source, category } = payload;

        // Write to ref synchronously so the product-change effect can read it
        if (imageUrl) injectedImageRef.current = imageUrl;

        // Set source filter so the product is visible in the list
        setActiveSource(source);

        // Set category filter to match product
        if (category) setSelectedCategory(category);

        // Set the product on the canvas (triggers the image-update effect)
        setSelectedProduct(overlayProduct);
        setSelectedVariant(null);
    }, []);

    return {
        logos,
        selectedLogo,
        selectedProduct,
        selectedProductImage,
        currentProductImages,
        currentImageIndex,
        productOptions: filteredProducts,
        mainProducts,
        partnerProducts,
        productCategories,
        selectedCategory,
        activeSource,
        searchTerm,
        isLoading,
        selectedVariant,
        canvasRef,
        containerRef,
        setSelectedLogo,
        setSelectedProduct,
        setSelectedProductImage,
        setSelectedCategory,
        setActiveSource,
        setSearchTerm,
        setSelectedVariant: selectVariant,
        setCurrentImageIndex,
        nextImage,
        prevImage,
        clearCategory,
        handleFileUpload,
        handleMouseDown,
        handleMouseMove,
        handleMouseUp,
        handleWheel,
        handleTouchStart,
        handleTouchMove,
        handleTouchEnd,
        rotateLogo,
        scaleLogo,
        deleteLogo,
        resetAll,
        captureAndSendQuote,
        injectProduct
    };
};